/**
 * Maps a landing URL into fixed tracking dimensions (CJT-15710).
 *
 * Partners pass the same meaning under different parameter names (`creative`, `utm_creative_ID`,
 * `teaser_id` are all "the platform's ad ID"). The mapping in `trackingDimensionsMapping.ts` says, per
 * partner (`utm_source` group), which parameter feeds which dimension. The result is pushed to the
 * dataLayer as `tracking`, so analytics tags only read it.
 *
 * Resolution per dimension: the partner's own parameters in order (an explicit mapping is authoritative),
 * then the recommended parameter, then the `*` entries. The first present, non-empty value wins. So a new
 * partner needs no mapping as long as it uses the recommended parameters. A dimension can skip some
 * utm_sources (`exclude_sources`) and give an extract pattern to entries that have none (`extract`).
 * Unfilled macros (`{creative}`, `{{ad.id}}`, …) are kept as the value and listed in `tracking_errors`.
 */
import { ParamSource, DimensionConfig, TrackingMapping } from '../interface/ITrackingMapping';
import mapping from './trackingDimensionsMapping';

export type TrackingDimensions = { [property: string]: string | string[] };

export const TRACKING_ERRORS_PROPERTY = 'tracking_errors';

/**
 * Parameters from the query string and the fragment (Taboola appends `#tblci=…`), keyed by lower-case
 * name. Values stay raw (not decoded) so the macro check sees them as they arrived.
 */
export function parseUrlParameters(url: string): { [name: string]: string } {
  const params: { [name: string]: string } = {};
  const queryStart = url.indexOf('?');
  const hashStart = url.indexOf('#');
  const parts: string[] = [];

  if (queryStart > -1) {
    parts.push(url.substring(queryStart + 1, hashStart > queryStart ? hashStart : url.length));
  }

  if (hashStart > -1) {
    parts.push(url.substring(hashStart + 1));
  }

  parts
    .join('&')
    .split('&')
    .forEach((pair) => {
      const separator = pair.indexOf('=');

      if (separator < 1) {
        return;
      }

      const name = decodeValue(pair.substring(0, separator)).toLowerCase();
      const value = pair.substring(separator + 1);

      if (value !== '' && !params.hasOwnProperty(name)) {
        params[name] = value;
      }
    });

  return params;
}

/** URL-decodes a parameter value (`+` as space); returns it undecoded when malformed. */
export function decodeValue(value: string): string {
  const withSpaces = value.replace(/\+/g, ' ');

  try {
    return decodeURIComponent(withSpaces);
  } catch (err) {
    return withSpaces;
  }
}

function findSourceGroup(config: TrackingMapping, utmSource: string): string {
  const source = (utmSource || '').toLowerCase();

  return (
    Object.keys(config.source_groups).filter((group) =>
      config.source_groups[group].some((member) => member.toLowerCase() === source)
    )[0] || ''
  );
}

function candidates(dimension: DimensionConfig, group: string): ParamSource[] {
  const entries: ParamSource[] = ((group && dimension.sources[group]) || []).concat(
    [dimension.recommended_param],
    dimension.sources['*'] || []
  );

  return dimension.extract
    ? entries.map((entry) =>
        typeof entry === 'string' ? { param: entry, extract: dimension.extract, flags: dimension.flags } : entry
      )
    : entries;
}

function isExcluded(dimension: DimensionConfig, utmSource: string): boolean {
  const source = (utmSource || '').toLowerCase();

  return !!source && (dimension.exclude_sources || []).some((excluded) => excluded.toLowerCase() === source);
}

type UrlParameters = { [name: string]: string };

interface ResolvedDimension {
  value: string;
  param: string;
  isMacro: boolean;
}

/** One candidate parameter: its decoded (and extracted) value, or null when it gives nothing usable. */
function resolveSource(
  source: ParamSource,
  params: UrlParameters,
  checkMacro: boolean,
  macro: RegExp
): ResolvedDimension | null {
  const param = typeof source === 'string' ? source : source.param;
  const raw = params[param.toLowerCase()];

  if (!raw) {
    return null;
  }

  const isMacro = checkMacro && macro.test(raw);
  let value = decodeValue(raw);

  if (!isMacro && typeof source !== 'string') {
    const match = value.match(new RegExp(source.extract, source.flags || ''));

    if (!match || !match[1]) {
      return null;
    }

    value = match[1];
  }

  return value.trim() === '' ? null : { value, param, isMacro };
}

/** The first candidate that gives a value wins. */
function resolveDimension(
  name: string,
  dimension: DimensionConfig,
  group: string,
  params: UrlParameters,
  config: TrackingMapping,
  macro: RegExp
): ResolvedDimension | null {
  const checkMacro = !config.macro_check_skip.includes(name);
  const sources = candidates(dimension, group);

  for (const source of sources) {
    const resolved = resolveSource(source, params, checkMacro, macro);

    if (resolved) {
      return resolved;
    }
  }

  return null;
}

export function mapTrackingDimensions(url: string, config: TrackingMapping = mapping): TrackingDimensions {
  const params = parseUrlParameters(url || '');
  const utmSource = params.utm_source && decodeValue(params.utm_source);
  const group = findSourceGroup(config, utmSource);
  const macro = new RegExp(config.macro_pattern);
  const result: TrackingDimensions = {};
  const errors: string[] = [];

  Object.keys(config.dimensions).forEach((name) => {
    const dimension = config.dimensions[name];
    const resolved = isExcluded(dimension, utmSource)
      ? null
      : resolveDimension(name, dimension, group, params, config, macro);

    if (!resolved) {
      return;
    }

    const { value, param, isMacro } = resolved;

    result[name] = dimension.normalize === 'uppercase' && !isMacro ? value.toUpperCase() : value;

    if (dimension.type_property) {
      result[dimension.type_property] = param;
    }

    if (isMacro) {
      errors.push(`${name}:${param}`);
    }
  });

  if (errors.length) {
    result[TRACKING_ERRORS_PROPERTY] = errors;
  }

  return result;
}
